import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, Popconfirm,
    notification, Typography, Tag, Tabs, Tooltip, Space, Select,
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ReloadOutlined, LoadingOutlined,
} from '@ant-design/icons';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Text } = Typography;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function AdminEmailTemplates() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, editing: null });
    const [previewModal, setPreviewModal] = useState({ open: false, template: null });
    const [saving, setSaving] = useState(false);
    const [perPage, setPerPage] = useState(10);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [form] = Form.useForm();

    const fetchTemplates = useCallback((page = 1, size = perPage) => {
        setLoading(true);
        adminApi.get('/admin/email-templates', { params: { page, per_page: size } })
            .then((res) => {
                const d = res.data.data;
                setTemplates(d.data);
                setPagination({ current: d.current_page, pageSize: d.per_page, total: d.total });
            })
            .catch(() => notification.error({ message: 'Failed to Load', description: 'Could not fetch email templates. Please try again.' }))
            .finally(() => setLoading(false));
    }, [perPage]);

    const handlePerPageChange = (val) => {
        setPerPage(val);
        fetchTemplates(1, val);
    };

    useEffect(() => { fetchTemplates(1); }, [fetchTemplates]);

    const openCreate = () => {
        form.resetFields();
        setModal({ open: true, editing: null });
    };

    const openEdit = (rec) => {
        form.setFieldsValue(rec);
        setModal({ open: true, editing: rec });
    };

    const handleSave = async (values) => {
        setSaving(true);
        try {
            if (modal.editing) {
                await adminApi.put(`/admin/email-templates/${modal.editing.id}`, values);
                notification.success({ message: 'Template Updated', description: 'The email template has been updated successfully.' });
            } else {
                await adminApi.post('/admin/email-templates', values);
                notification.success({ message: 'Template Created', description: 'The new email template has been created successfully.' });
            }
            setModal({ open: false, editing: null });
            fetchTemplates();
        } catch (err) {
            const errors = err.response?.data?.errors;
            if (errors) {
                Object.values(errors).flat().forEach((msg) =>
                    notification.error({ message: 'Validation Error', description: msg })
                );
            } else {
                notification.error({ message: 'Failed to Save', description: 'Could not save the email template. Please try again.' });
            }
        } finally {
            setSaving(false);
        }
    };

    const deleteTemplate = async (id) => {
        try {
            await adminApi.delete(`/admin/email-templates/${id}`);
            notification.success({ message: 'Template Deleted', description: 'The email template has been removed successfully.' });
            fetchTemplates();
        } catch (_) {
            notification.error({ message: 'Failed to Delete', description: 'Could not delete the email template. Please try again.' });
        }
    };

    const columns = [
        {
            title: 'Key',
            dataIndex: 'key',
            render: (v) => <Text code style={{ color: 'var(--color-primary)' }}>{v}</Text>,
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            ellipsis: true,
        },
        {
            title: 'Updated',
            dataIndex: 'updated_at',
            width: 130,
            render: (v) => dayjs(v).format('D MMM YYYY'),
        },
        {
            title: 'Actions',
            width: 140,
            render: (_, row) => (
                <Space>
                    <Tooltip title="Preview">
                        <Button
                            type="text" size="small" icon={<EyeOutlined />}
                            onClick={() => setPreviewModal({ open: true, template: row })}
                            style={{ color: '#3B82F6' }}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            type="text" size="small" icon={<EditOutlined />}
                            onClick={() => openEdit(row)}
                            style={{ color: 'var(--color-primary)' }}
                        />
                    </Tooltip>
                    <Popconfirm title="Delete this template?" onConfirm={() => deleteTemplate(row.id)}
                        okButtonProps={{ danger: true }}>
                        <Tooltip title="Delete">
                            <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <AdminLayout title="Email Templates">
            <div className="admin-page-header">
                <h1 className="admin-page-title">Email Templates</h1>
                <p className="admin-page-subtitle">Manage the email templates used across the platform</p>
            </div>

            <div className="admin-card">
                <div className="admin-card-header">
                    <div />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Per page:</span>
                        <Select
                            value={perPage}
                            onChange={handlePerPageChange}
                            style={{ width: 80 }}
                            options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: n }))}
                        />
                        <Button icon={<ReloadOutlined />} onClick={() => fetchTemplates(pagination.current, perPage)}>Refresh</Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                            style={{ background: 'var(--gradient-primary)', border: 'none' }}
                        >
                            New Template
                        </Button>
                    </div>
                </div>

                <Table
                    className="admin-table"
                    dataSource={templates}
                    columns={columns}
                    rowKey="id"
                    loading={{ spinning: loading, indicator: <LoadingOutlined style={{ fontSize: 32, color: '#AD1457' }} spin /> }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: false,
                        showTotal: (total) => `${total} templates`,
                        onChange: (page) => fetchTemplates(page, perPage),
                    }}
                    scroll={{ x: 500 }}
                    size="middle"
                />
            </div>

            {/* Create / Edit modal */}
            <Modal
                title={modal.editing ? 'Edit Email Template' : 'New Email Template'}
                open={modal.open}
                onCancel={() => setModal({ open: false, editing: null })}
                onOk={() => form.submit()}
                okText={modal.editing ? 'Save Changes' : 'Create'}
                confirmLoading={saving}
                width={Math.min(780, window.innerWidth - 32)}
                centered
                styles={{ body: { padding: '16px 24px', maxHeight: '80vh', overflowY: 'auto' } }}
            >
                <Form form={form} layout="vertical" onFinish={handleSave}>
                    <Form.Item
                        name="key"
                        label={
                            <span>
                                Template Key{' '}
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    (used in code, e.g. welcome_email)
                                </Text>
                            </span>
                        }
                        rules={[
                            { required: true, message: 'Key is required' },
                            { pattern: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers and underscores only' },
                        ]}
                    >
                        <Input placeholder="e.g. welcome_email" disabled={!!modal.editing} />
                    </Form.Item>

                    <Form.Item
                        name="subject"
                        label="Email Subject"
                        rules={[{ required: true, message: 'Subject is required' }]}
                    >
                        <Input placeholder="e.g. Welcome to Oottan.com!" />
                    </Form.Item>

                    <Tabs
                        items={[
                            {
                                key: 'html',
                                label: 'HTML Body',
                                children: (
                                    <Form.Item name="body_html" rules={[{ required: true, message: 'HTML body is required' }]} style={{ margin: 0 }}>
                                        <TextArea
                                            rows={14}
                                            placeholder="<html><body>...</body></html>"
                                            style={{ fontFamily: 'monospace', fontSize: 13 }}
                                        />
                                    </Form.Item>
                                ),
                            },
                            {
                                key: 'text',
                                label: 'Plain Text',
                                children: (
                                    <Form.Item name="body_text" rules={[{ required: true, message: 'Plain text body is required' }]} style={{ margin: 0 }}>
                                        <TextArea
                                            rows={14}
                                            placeholder="Plain text version of the email..."
                                            style={{ fontFamily: 'monospace', fontSize: 13 }}
                                        />
                                    </Form.Item>
                                ),
                            },
                        ]}
                    />
                </Form>
            </Modal>

            {/* Preview modal */}
            <Modal
                title={`Preview: ${previewModal.template?.subject || ''}`}
                open={previewModal.open}
                onCancel={() => setPreviewModal({ open: false, template: null })}
                footer={null}
                width={Math.min(720, window.innerWidth - 32)}
                centered
            >
                {previewModal.template && (
                    <div>
                        <div style={{ marginBottom: 8 }}>
                            <Tag color="blue">{previewModal.template.key}</Tag>
                        </div>
                        <div
                            style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}
                            dangerouslySetInnerHTML={{ __html: previewModal.template.body_html }}
                        />
                    </div>
                )}
            </Modal>
        </AdminLayout>
    );
}
